import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

const BASE_HEADERS: Record<string, string> = {
  // MUST be 'noChange'. camelCase rewrites MongoDB's `_id` → `id` and FK fields
  // `category_id`/`user_id`/`parent_id`/`product_id` → camelCase, which the DTOs/templates
  // (all snake_case `_id`) never read — that's what produced `/get-by-id/undefined`.
  'x-am-response-case': 'noChange',
  'x-am-content-type-response': 'application/json',
};

/**
 * Auth wiring (COORDINATION.md §4 [C 15:36/15:41]):
 *  - /admin-login (the one custom API) → public, no token.
 *  - everything else (native schema CRUD) → AM token as `x-am-authorization` (+ the DB-user token
 *    as `x-am-user-authorization`, matching the book_era reference). Optional `x-am-secret`.
 * Both tokens come from a single Admin Login (Get Token was removed), so there's no refresh dance:
 * on a 401/403 against an authed call we clear the session and route to /login.
 * Only requests to the configured backend host are touched.
 */
export const apiHeadersInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiBaseUrl)) return next(req);
  const auth = inject(AuthService);
  const router = inject(Router);

  // Public login — no token attached.
  if (req.url.includes('/admin-login')) {
    return next(req.clone({ setHeaders: BASE_HEADERS }));
  }

  const headers = { ...BASE_HEADERS };
  const apiToken = auth.apiToken();
  const userToken = auth.userToken();
  if (apiToken) headers['x-am-authorization'] = apiToken;
  if (userToken) headers['x-am-user-authorization'] = userToken;
  if (environment.apiSecret) headers['x-am-secret'] = environment.apiSecret;

  const wasAuthed = !!apiToken;
  return next(req.clone({ setHeaders: headers })).pipe(
    catchError((err) => {
      const status = (err as { status?: number })?.status;
      // Only bounce to login when an authed call is rejected — lets dev view screens unauthenticated.
      if ((status === 401 || status === 403) && wasAuthed) {
        auth.logout();
        router.navigate(['/login']);
      }
      return throwError(() => err);
    }),
  );
};
